import type { Instance, Props } from "tippy.js"
import { ReactRenderer } from "@tiptap/react"
import tippy from "tippy.js"

import type { EmojiSearchResult } from "@productlane/lib/src/emoji"

import type { EmojiSuggestionCommandProps } from "./emoji-suggestion"
import type {
	EmojiOnKeyDownHandler,
	EmojiOnKeyDownHandlerSetter,
} from "./types"
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion"
import EmojiList from "./emoji-list"

type EmojiSuggestionProps = SuggestionProps<
	EmojiSearchResult,
	EmojiSuggestionCommandProps
>

export const emojiSuggestionRenderer: NonNullable<
	SuggestionOptions<EmojiSearchResult, EmojiSuggestionCommandProps>["render"]
> = () => {
	let component: ReactRenderer<unknown, EmojiSuggestionProps> | null = null
	let popup: Instance<Props>[] | null = null
	let onKeyDownHandler: EmojiOnKeyDownHandler = () => false
	const setOnKeyDownHandler: EmojiOnKeyDownHandlerSetter = (handler) => {
		onKeyDownHandler = handler
	}

	let dismissed = false

	const syncVisibility = (items: EmojiSearchResult[]) => {
		if (dismissed || items.length === 0) {
			popup?.[0]?.hide()
			return
		}
		popup?.[0]?.show()
	}

	return {
		onStart: (props) => {
			component = new ReactRenderer(EmojiList, {
				props: { ...props, setOnKeyDownHandler },
				editor: props.editor,
			})

			if (!props.clientRect) return

			// @ts-expect-error tippy types are wrong
			popup = tippy("body", {
				getReferenceClientRect: props.clientRect,
				appendTo: () => document.body,
				content: component.element,
				showOnCreate: true,
				interactive: true,
				trigger: "manual",
				placement: "bottom-start",
			})

			syncVisibility(props.items)
		},

		onUpdate(props) {
			component?.updateProps({ ...props, setOnKeyDownHandler })

			const { clientRect } = props
			if (clientRect) {
				popup?.[0]?.setProps({
					getReferenceClientRect: () => clientRect() ?? new DOMRect(),
				})
			}

			syncVisibility(props.items)
		},

		onKeyDown(props) {
			if (props.event.key === "Escape") {
				dismissed = true
				popup?.[0]?.hide()
				return true
			}

			return onKeyDownHandler(props)
		},

		onExit() {
			popup?.[0]?.destroy()
			component?.destroy()
			popup = null
			component = null
			dismissed = false
		},
	}
}
